import { SerializedError } from "@reduxjs/toolkit";
import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { appConfig } from "@webcore/config/appConfig.ts";
import { ApiDataResponse, ApiResponse, ApiResponseBase, ApiResultType } from "./models.ts";
// import { decrementUpdatesInFlight, incrementUpdatesInFlight, logout } from "@/store/slices/userContextSlice.ts";
import { getClientInstanceId } from "@webcore/services/clientInstanceId.ts";
import Log from "@webcore/services/log/log.ts";

const logger = Log.createLogger("API");

let requestIndex = 0;

export function isApiResponseBase(obj: any): obj is ApiResponseBase {
  return obj && typeof obj === "object" && "result" in obj;
}

/**
 * Extracts a user-facing message from an RTK Query mutation error. ApiBadRequestException
 * bodies arrive as a bare JSON string (BadRequestObjectResult(Message) on the backend); falls
 * back to `fallback` for any other shape (network errors, unexpected bodies).
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === "string" && data.trim().length > 0) {
      return data;
    }
    if (data && typeof data === "object" && "message" in data && typeof (data as { message?: unknown }).message === "string") {
      return (data as { message: string }).message;
    }
  }
  return fallback;
}

/**
 * A user-defined type guard to check if an object
 * is an ApiDataResponse<TData>.
 */
export function isApiDataResponse<TData>(obj: any): obj is ApiDataResponse<TData> {
  // At runtime, just check if `data` property exists (or is defined as needed).
  return isApiResponseBase(obj) && "data" in obj;
}

/**
 * True when a query or mutation failed because the caller is not permitted to make it, so the
 * caller can tell "you don't have access to this" apart from "this could not be loaded". A
 * SerializedError is raised client-side without a response and so carries no status.
 */
export function isForbiddenError(error: FetchBaseQueryError | SerializedError | undefined): boolean {
  return error !== undefined && "status" in error && error.status === 403;
}

/**
 * True when a query failed because what it asked for no longer exists, so the caller can tell
 * "this is gone" apart from "this could not be loaded".
 */
export function isNotFoundError(error: FetchBaseQueryError | SerializedError | undefined): boolean {
  return error !== undefined && "status" in error && error.status === 404;
}

const apiPathPrefix = '/v1';

// appConfig.apiBaseUrl is the same-origin `/api` reverse proxy on the running frontend's
// own origin, which forwards `/api/v1/x` to the backend as `/v1/x`. Same-origin calls
// keep auth cookies host-only per frontend (e.g. the read-only MFE cookie on the MFE origin).
const rawBaseQuery = fetchBaseQuery({
  baseUrl: appConfig.apiBaseUrl + apiPathPrefix,
  credentials: "include",
  prepareHeaders: (headers) => {
    headers.set('X-Client-Instance-Id', getClientInstanceId());
    return headers;
  },
});

export type ApiExtraOptions = {
  isMutationOverride: boolean;
  treat404AsNull: boolean; // If true, 404 responses return null data instead of error; defaults to false.
}

export type BaseQueryContext = {
  onNewUpdateInFlight: (() => void) | null;
  onUpdateCompleted: (() => void) | null;
  onUnauthorized: (() => void) | null;
}

let baseQueryContext: BaseQueryContext = {
  onNewUpdateInFlight: null,
  onUpdateCompleted: null,
  onUnauthorized: null
}

export function setBaseQueryContext(contextUpdate: Partial<BaseQueryContext>) {
  baseQueryContext = { ...baseQueryContext, ...contextUpdate };
}

/**
 * A custom base query that:
 * 1) Uses RTK Query's fetchBaseQuery under the hood.
 * 2) Checks the `result` field. If it's not "Ok", return an error.
 * 3) If the response is good, rewrite `result.data` to be the API's `.data` field.
 *    So endpoints see only `payload` as the top-level data.
 */
export const baseQueryWithResultCheck: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    Partial<ApiExtraOptions>
> = async (args, api, suppliedExtraOptions) => {
  const extraOptions: ApiExtraOptions = {
    isMutationOverride: true,
    treat404AsNull: false,
    ...suppliedExtraOptions
  };

  const thisRequestIndex = requestIndex;

  ++requestIndex;

  logger.debug(`baseQuery() starting request #${thisRequestIndex}`, { args });

  const fetchArgs = args as FetchArgs;

  const isMutation = fetchArgs.method &&
      fetchArgs.method.toLowerCase() !== "get" && extraOptions.isMutationOverride;

  if (isMutation) {
    // api.dispatch(incrementUpdatesInFlight());
    baseQueryContext.onNewUpdateInFlight?.();
  }

  const result = await rawBaseQuery(args, api, extraOptions);

  if (isMutation) {
    // api.dispatch(decrementUpdatesInFlight());
    baseQueryContext.onUpdateCompleted?.();
  }

  // Handle 404 as null data if requested (404 comes through as valid response due to validateStatus)
  if (extraOptions.treat404AsNull && result.error !== undefined && result.error.status === 404) {
    return {
      data: null
    };
  }

  if (result.error) {
    console.error(`Error from API response: status=${result.error.status}`, result.error);

    if (result.error.status === 401) {
      // unauthorized - cookie expired, remove usercontext & set the app state to logged out, the app will then flow
      // from this state to take the user to log in.
      // logger.debug("401 result, should redirect to login.");

      // api.dispatch(logout());
      baseQueryContext.onUnauthorized?.();
    }
  }

  if (result.data) {
    logger.debug("got result data.", result.data);

    const response = result.data as ApiResponseBase;

    // If the server says "result" is not "Ok", treat it as an error
    if (response.result !== ApiResultType.Ok) {
      console.error("response was not a success response code.", response.result);

      // if (response.result === ApiResultType.QuotaLimit) {
      //   const error = new QuotaError({ error: ErrorType.OwnedTeamQuotaExceeded });
      //
      //   api.dispatch(setError(error.getStoredError()));
      // }

      return {
        error: {
          status: 500, // Or any code you prefer
          data: response
        },
      };
    }

    const successResponse = response as ApiResponse;

    if (isApiDataResponse<any>(successResponse)) {
      result.data = successResponse.data;
      logger.debug("Received a success response with data.", successResponse.data);
    }
    else {
      result.data = null;
    }

    logger.debug(`request processing completes. request #${thisRequestIndex}`);
  }

  return result;
};
